import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { FileText, Search, BookOpen, Calendar, Plus } from '../components/Icons';
import { getStoolInfo } from '../utils/stoolInfo';
import styles from './Dashboard.module.css';

// kapitalisasi tiap kata: "abdurrahman zaidan" → "Abdurrahman Zaidan"
function titleCase(str = '') {
  return str.replace(/\b\w/g, c => c.toUpperCase());
}
// inisial: "Adzriel Erina" → "A"
function initial(name = '') {
  return (name.trim()[0] || '?').toUpperCase();
}

function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isID = i18n.language === 'id';

  const [children, setChildren] = useState([]);
  const [activeChildId, setActiveChildId] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // ambil daftar anak (id + name minimal)
  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase
        .from('children').select('id, name')
        .eq('caregiver_id', user.id)
        .order('created_at', { ascending: false });
      if (active && data) {
        setChildren(data);
        if (data.length > 0) setActiveChildId(data[0].id);
      }
    })();
    return () => { active = false; };
  }, [user.id]);

  // ambil log milik anak aktif
  const fetchLogs = useCallback(async () => {
    if (!activeChildId) { setRecentLogs([]); setLoading(false); return; }
    setLoading(true);
    const { data } = await supabase
      .from('gi_logs').select('*')
      .eq('caregiver_id', user.id)
      .eq('child_id', activeChildId)
      .order('date', { ascending: false })
      .limit(3);
    setRecentLogs(data || []);
    setLoading(false);
  }, [user.id, activeChildId]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const name = titleCase(user?.user_metadata?.full_name || user?.email || '');
  const activeChild = children.find(c => c.id === activeChildId);
  const latestLog = recentLogs[0];

  const menu = [
    { icon: <FileText size={24} strokeWidth={1.5} aria-hidden="true" />, label: t('nav.log'), desc: t('dashboard.logDesc'), to: '/log' },
    { icon: <Search size={24} strokeWidth={1.5} aria-hidden="true" />, label: t('nav.screening'), desc: t('dashboard.screeningDesc'), to: '/screening' },
    { icon: <BookOpen size={24} strokeWidth={1.5} aria-hidden="true" />, label: t('nav.info'), desc: t('dashboard.infoDesc'), to: '/info' },
  ];

  const toneCls = (tone) =>
    tone === 'danger' ? styles.toneDanger : tone === 'warn' ? styles.toneWarn : styles.toneGood;

  return (
    <div className={styles.page}>
      {/* HERO */}
      <div className={styles.hero}>
        <div>
          <h1 className={styles.greeting}>
            {t('dashboard.greeting')}, {name} 👋
          </h1>
          <p className={styles.subtitle}>{t('dashboard.subtitle')}</p>
        </div>

        {children.length > 0 && (
          <div className={styles.childChips} role="group"
            aria-label={isID ? 'Pilih anak' : 'Select child'}>
            {children.map(c => {
              const on = c.id === activeChildId;
              return (
                <button key={c.id}
                  className={`${styles.childChip} ${on ? styles.childChipActive : ''}`}
                  aria-pressed={on}
                  onClick={() => setActiveChildId(c.id)}>
                  <span className={styles.childAvatar}>{initial(c.name)}</span>
                  {c.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* RINGKASAN KONDISI ANAK AKTIF */}
      {activeChild && (
        <div className={styles.summaryCard}>
          <div className={styles.summaryHead}>
            {isID ? `Kondisi terkini ${activeChild.name}` : `${activeChild.name}'s latest condition`}
          </div>

          {latestLog ? (
            <div className={styles.summaryGrid}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>{isID ? 'Catatan terakhir' : 'Last record'}</span>
                <span className={styles.summaryValue}>{latestLog.date}</span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>{isID ? 'Bentuk feses (Tinja)' : 'Stool form'}</span>
                <span className={styles.summaryValue}>
                  {isID ? 'Tipe' : 'Type'} {latestLog.stool_type ?? '-'}
                </span>
                {latestLog.stool_type && (
                  <span className={`${styles.summaryNote} ${toneCls(getStoolInfo(latestLog.stool_type, isID).tone)}`}>
                    {getStoolInfo(latestLog.stool_type, isID).label}
                  </span>
                )}
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>{isID ? 'Keluhan' : 'Complaint'}</span>
                <span className={styles.summaryValue}>
                  {latestLog.stool_complaint
                    ? (isID ? 'Ada keluhan' : 'Has complaint')
                    : (isID ? 'Tidak ada' : 'None')}
                </span>
                {latestLog.stool_complaint && (
                  <span className={styles.summaryComplaint}>{latestLog.stool_complaint}</span>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.summaryEmpty}>
              <p className={styles.summaryEmptyText}>
                {isID
                  ? `Belum ada catatan untuk ${activeChild.name}.`
                  : `No records yet for ${activeChild.name}.`}
              </p>
              <button className={styles.primaryBtn} onClick={() => navigate('/log')}>
                <Plus size={16} aria-hidden="true" />
                {isID ? 'Tambah catatan pertama' : 'Add first record'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* KARTU MENU */}
      <div className={styles.menuGrid}>
        {menu.map((m, i) => (
          <button key={i} className={styles.menuCard} onClick={() => navigate(m.to)}>
            <div className={styles.menuIcon}>{m.icon}</div>
            <div className={styles.menuTitle}>{m.label}</div>
            <div className={styles.menuDesc}>{m.desc}</div>
          </button>
        ))}
      </div>

      {/* CATATAN TERBARU (anak aktif) */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>
          {activeChild
            ? (isID ? `Catatan Terbaru — ${activeChild.name}` : `Recent Records — ${activeChild.name}`)
            : t('dashboard.recentLogs')}
        </h2>

        {loading ? (
          <p className={styles.loading}>{t('common.loading')}</p>
        ) : recentLogs.length === 0 ? (
          <div className={styles.emptyCard}>
            <p className={styles.emptyText}>{t('dashboard.noLogs')}</p>
            <button className={styles.primaryBtn} onClick={() => navigate('/log')}>
              <Plus size={16} aria-hidden="true" />
              {t('dashboard.addFirstLog')}
            </button>
          </div>
        ) : (
          <div className={styles.logList}>
            {recentLogs.map(log => {
              const info = log.stool_type ? getStoolInfo(log.stool_type, isID) : null;
              return (
                <div key={log.id} className={styles.logCard}>
                  <div className={styles.logDate}>
                    <Calendar size={14} aria-hidden="true" />{log.date}
                  </div>
                  <div className={styles.logRow}>
                    <span className={styles.logLabel}>{isID ? 'Bentuk feses:' : 'Stool form:'}</span>
                    <span className={styles.logValue}>{isID ? 'Tipe' : 'Type'} {log.stool_type ?? '-'}</span>
                    {info && <span className={`${styles.logNote} ${toneCls(info.tone)}`}>· {info.label}</span>}
                  </div>
                  <div className={styles.logRow}>
                    <span className={styles.logLabel}>{isID ? 'Keluhan:' : 'Complaint:'}</span>
                    <span className={`${styles.logBadge} ${log.stool_complaint ? styles.badgeYes : styles.badgeNo}`}>
                      {log.stool_complaint
                        ? (isID ? 'Ada' : 'Yes')
                        : (isID ? 'Tidak ada' : 'None')}
                    </span>
                  </div>
                  {log.stool_complaint && (
                    <div className={styles.logComplaint}>{log.stool_complaint}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;